import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowDown, ArrowUp, Eye, Image as ImageIcon, Link2, Loader2, Monitor, Plus, Save, Smartphone, Trash2, Upload,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, apiError, fileUrl } from "@/lib/api";
import { AdminCard, Field, inputCls } from "@/pages/admin/ui";

const EMPTY_ITEM = {
  desktop_url: "", desktop_kind: "image",
  mobile_url: "", mobile_kind: "image",
  link_url: "", duration_seconds: 6, is_active: true,
};

const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime";

function MediaSlot({ label, icon: Icon, url, kind, uploading, onPick, onClear, testId }) {
  return (
    <div className="rounded-xl border border-line bg-white p-3">
      <span className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
        <Icon className="h-3.5 w-3.5" /> {label}
      </span>
      <div className="relative flex h-24 items-center justify-center overflow-hidden rounded-lg bg-slate-900">
        {url ? (
          kind === "video" ? (
            <video src={fileUrl(url)} muted loop autoPlay playsInline className="h-full w-full object-cover" />
          ) : (
            <img src={fileUrl(url)} alt="" className="h-full w-full object-cover" />
          )
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-500"><ImageIcon className="h-6 w-6" /><span className="text-[10px]">Aucun média</span></div>
        )}
        {url && (
          <button type="button" onClick={onClear} data-testid={`${testId}-clear`}
            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-rose-500 shadow">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <button type="button" onClick={onPick} disabled={uploading} data-testid={`${testId}-btn`}
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-line py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand disabled:opacity-60">
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {uploading ? "Téléversement…" : "Téléverser"}
      </button>
    </div>
  );
}

export default function AdminPublications() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(""); // `${index}-desktop` | `${index}-mobile`
  const fileRefs = useRef({});

  useEffect(() => {
    api.get("/admin/publications").then(({ data }) => setCfg({
      enabled: !!data.enabled,
      items: (data.items || []).map((s) => ({ ...EMPTY_ITEM, ...s })),
    })).catch((e) => toast.error(apiError(e)));
  }, []);

  const setItem = (i, k, v) => setCfg((c) => ({ ...c, items: c.items.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const addItem = () => setCfg((c) => ({ ...c, items: [...c.items, { ...EMPTY_ITEM }] }));
  const removeItem = (i) => setCfg((c) => ({ ...c, items: c.items.filter((_, j) => j !== i) }));
  const move = (i, dir) => setCfg((c) => {
    const j = i + dir;
    if (j < 0 || j >= c.items.length) return c;
    const items = [...c.items];
    [items[i], items[j]] = [items[j], items[i]];
    return { ...c, items };
  });

  const upload = async (i, slot, file) => {
    if (!file) return;
    setUploading(`${i}-${slot}`);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      const kind = data.kind === "video" ? "video" : "image";
      setCfg((c) => ({ ...c, items: c.items.map((s, j) => (j === i ? { ...s, [`${slot}_url`]: data.url, [`${slot}_kind`]: kind } : s)) }));
      toast.success("Média téléversé ✅");
    } catch (e) {
      toast.error(apiError(e, "Upload impossible"));
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/publications", {
        enabled: cfg.enabled,
        items: cfg.items.map((s) => ({
          id: s.id || "",
          desktop_url: s.desktop_url || "", desktop_kind: s.desktop_kind || "image",
          mobile_url: s.mobile_url || "", mobile_kind: s.mobile_kind || "image",
          link_url: s.link_url || "", duration_seconds: Number(s.duration_seconds) || 6,
          is_active: s.is_active !== false,
        })),
      });
      setCfg({ enabled: !!data.enabled, items: (data.items || []).map((s) => ({ ...EMPTY_ITEM, ...s })) });
      toast.success("Publications enregistrées ✅");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-publications-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">NOS PUBLICATIONS (bannière en-tête)</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Grande bannière affichée sous la barre de bienvenue, au-dessus du logo. Ajoutez des images ou vidéos, un média différent
            pour ordinateur et pour mobile/tablette, une durée d'affichage par publication, puis réorganisez-les. Elles défilent automatiquement.
          </p>
        </div>
        <button onClick={save} disabled={saving} data-testid="publications-save"
          className="flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
        </button>
      </div>

      <AdminCard title="Réglage général" description="Active ou désactive toute la bannière « NOS PUBLICATIONS ».">
        <div className="flex items-center justify-between rounded-xl border border-line bg-surface px-4 py-3">
          <div>
            <p className="text-sm font-bold text-ink">Afficher la bannière sur le site</p>
            <p className="text-xs text-slate-500">Visible entre la barre de bienvenue et le logo/recherche de l'en-tête.</p>
          </div>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="publications-enabled" />
        </div>
      </AdminCard>

      <AdminCard
        title={`Publications (${cfg.items.length})`}
        description="Formats conseillés — Ordinateur : bannière large ~ 1128 × 150 px. Mobile/tablette : ~ 375 × 120 px. Images (JPG, PNG, WebP, GIF) ou vidéos (MP4, WebM)."
        action={(
          <button onClick={addItem} data-testid="publications-add"
            className="flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand">
            <Plus className="h-3.5 w-3.5" /> Ajouter une publication
          </button>
        )}
      >
        {cfg.items.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">Aucune publication. Cliquez sur « Ajouter une publication » pour commencer.</p>
        ) : (
          <div className="space-y-5">
            {cfg.items.map((s, i) => (
              <div key={s.id || i} className="rounded-2xl border border-line bg-slate-50/60 p-4" data-testid={`publication-${i}`}>
                <div className="mb-3 flex items-center justify-between">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-xs font-black text-white">{i + 1}</span>
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`publication-up-${i}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand disabled:opacity-40">
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === cfg.items.length - 1} data-testid={`publication-down-${i}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand disabled:opacity-40">
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <label className="ml-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      Actif <Switch checked={s.is_active !== false} onCheckedChange={(v) => setItem(i, "is_active", v)} data-testid={`publication-active-${i}`} />
                    </label>
                    <button type="button" onClick={() => removeItem(i)} data-testid={`publication-remove-${i}`}
                      className="ml-1 flex h-8 w-8 items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <input ref={(el) => (fileRefs.current[`${i}-desktop`] = el)} type="file" className="hidden" accept={ACCEPT}
                    data-testid={`publication-desktop-file-${i}`} onChange={(e) => upload(i, "desktop", e.target.files?.[0])} />
                  <MediaSlot label="Ordinateur (desktop)" icon={Monitor} url={s.desktop_url} kind={s.desktop_kind}
                    uploading={uploading === `${i}-desktop`} testId={`publication-desktop-${i}`}
                    onPick={() => fileRefs.current[`${i}-desktop`]?.click()}
                    onClear={() => setCfg((c) => ({ ...c, items: c.items.map((x, j) => (j === i ? { ...x, desktop_url: "" } : x)) }))} />

                  <input ref={(el) => (fileRefs.current[`${i}-mobile`] = el)} type="file" className="hidden" accept={ACCEPT}
                    data-testid={`publication-mobile-file-${i}`} onChange={(e) => upload(i, "mobile", e.target.files?.[0])} />
                  <MediaSlot label="Mobile / tablette" icon={Smartphone} url={s.mobile_url} kind={s.mobile_kind}
                    uploading={uploading === `${i}-mobile`} testId={`publication-mobile-${i}`}
                    onPick={() => fileRefs.current[`${i}-mobile`]?.click()}
                    onClear={() => setCfg((c) => ({ ...c, items: c.items.map((x, j) => (j === i ? { ...x, mobile_url: "" } : x)) }))} />
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <Field label="Lien au clic (optionnel)">
                    <div className="relative">
                      <Link2 className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                      <input className={`${inputCls} pl-9`} placeholder="/promotions ou https://…" value={s.link_url}
                        onChange={(e) => setItem(i, "link_url", e.target.value)} data-testid={`publication-link-${i}`} />
                    </div>
                  </Field>
                  <Field label="Durée d'affichage (secondes)">
                    <input type="number" min={1} max={120} className={inputCls} value={s.duration_seconds}
                      onChange={(e) => setItem(i, "duration_seconds", e.target.value)} data-testid={`publication-duration-${i}`} />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 flex items-center gap-2 rounded-xl bg-brand-light px-3 py-2 text-xs font-semibold text-brand">
          <Eye className="h-3.5 w-3.5" /> Astuce : intégrez le texte et les visuels directement dans l'image/vidéo (comme le modèle « NOS PUBLICATIONS ») pour un rendu parfait sans déformation.
        </p>
      </AdminCard>
    </div>
  );
}
